import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getAuthToken } from '../utils/cookieUtils';
import { getUserId } from '../utils/tokenUtils';
import { API_CONFIG } from '../utils/constants';
import axios from 'axios';

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);


export const createUserNotificationToken = async () => {
    try {
        // Guard: environment support
        if (typeof window === 'undefined' || !('Notification' in window)) {
            console.warn('[FCM] Web Notifications are not supported in this environment');
            return false;
        }

        // Only request permission if not already decided
        let permission = Notification.permission;
        if (permission === 'default') {
            permission = await Notification.requestPermission();
        }

        if (permission !== 'granted') {
            console.warn('[FCM] Notification permission not granted');
            return false;
        }

        // VAPID key fallback (Vite exposes only VITE_* by default)
        const vapidKey = import.meta.env.VITE_VAPID_KEY;
        if (!vapidKey) {
            console.warn('[FCM] Missing VAPID key (VITE_VAPID_KEY). Skipping token registration.');
            return false;
        }

        // Use service worker registration if available (prod), but tolerate missing in dev
        let swReg;
        if ('serviceWorker' in navigator) {
            try {
                swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
            } catch (_) {
                swReg = undefined;
            }
        }

        const tokenOptions = { vapidKey };
        if (swReg) {
            tokenOptions.serviceWorkerRegistration = swReg;
        }

        const token = await getToken(messaging, tokenOptions);
        if (!token) {
            console.warn('[FCM] No FCM token retrieved');
            return false;
        }

        const auth_token = getAuthToken();
        const userId = getUserId();

        if (!auth_token) {
            console.warn('[FCM] Missing auth token; skipping push token registration');
            return false;
        }
        if (!userId) {
            console.warn('[FCM] Missing user id; skipping push token registration');
            return false;
        }

        try {
            await axios.post(
                API_CONFIG.BASE_URL + API_CONFIG.NOTIFICATIONS.POST_FCM_TOKEN,
                { fcmToken: token, userId },
                {
                    headers: {
                        'Authorization': `Bearer ${auth_token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
        } catch (postErr) {
            // Don't surface as an error that breaks the app; just warn
            const status = postErr?.response?.status;
            console.warn('[FCM] Failed to register push token with backend', { status, error: postErr });
            return false;
        }

        return true;
    } catch (err) {
        console.warn('[FCM] Error during token setup', err);
        return false;
    }
};



export const getFcmToken = async () => {
    try {
        const vapidKey = import.meta.env.VITE_VAPID_KEY;
        if (!vapidKey) {
            console.warn('[FCM] Missing VAPID key (VITE_VAPID_KEY).');
            return null;
        }
        const token = await getToken(messaging, { vapidKey });
        return token;
    } catch (err) {
        console.warn('[FCM] Error getting FCM token', err);
        return null;
    }
};

export const sendTestNotification = (title, body, icon) => {
    if (!('Notification' in window)) {
        alert('This browser does not support notifications.');
        return;
    }

    const showNotification = () => {
        new Notification(title, {
            body: body,
            icon: icon || '/icons/Icon-192.png',
        });
    };

    if (Notification.permission === 'granted') {
        showNotification();
    } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
                showNotification();
            }
        });
    }
};

export const checkNotificationPermission = () => {
    return Notification.permission;
};

export const askNotificationPermission = async () => {
    if (!('Notification' in window)) {
        return 'unsupported';
    }

    try {
        const permission = await Notification.requestPermission();
        return permission;
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        return 'error';
    }
};



onMessage(messaging, (payload) => {
    navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(
            payload.notification?.title || payload.data?.title || 'Notification',
            {
                body: payload.notification?.body || payload.data?.body || '',
                icon: '/logo_96.png',
                  data: {
                    url: payload.data?.path  
                },

                actions: [
                    { action: 'view', title: 'Open' }
                ]
            }
        );
    });
});

